import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/base/Breadcrumb';
import NewsCard from '@/components/feature/NewsCard';
import { news } from '@/mocks/news';
import { usePageSeo } from '@/hooks/usePageSeo';

const LOAD_STEP = 10;

type SortKey = 'latest' | 'hot';

export default function NewsCategory() {
  usePageSeo({
    title: '小仓优子最新动态_新闻资讯与活动报道',
    description:
      '汇集小仓优子近期新闻、活动资讯、媒体专访与作品动态，支持按最新发布或最热阅读排序，及时了解她的近况与相关报道。',
    keywords: '小仓优子最新动态,小仓优子新闻,艺人资讯,活动报道,娱乐新闻',
  });

  const [sort, setSort] = useState<SortKey>('latest');
  const [visible, setVisible] = useState(LOAD_STEP);

  const sorted = useMemo(() => {
    const list = [...news];
    list.sort((a, b) => (sort === 'latest' ? b.date.localeCompare(a.date) : b.views - a.views));
    return list;
  }, [sort]);

  const visibleItems = sorted.slice(0, visible);
  const hasMore = visible < sorted.length;

  const handleSort = (s: SortKey) => {
    setSort(s);
    setVisible(LOAD_STEP);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full bg-gradient-to-br from-accent-50 via-background-50 to-secondary-50 border-b border-background-200">
          <div className="w-full px-4 md:px-8 py-10 md:py-14">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '新闻动态' }]} />
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mt-5">小仓优子最新动态</h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mt-4 max-w-3xl">
              在这里，你可以看到小仓优子近期的作品动态、活动资讯、媒体专访与生活花絮。我们会持续整理与更新，让每一份关注都能第一时间得到回应。内容按时间与热度排序，方便你快速浏览。
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div className="flex items-center bg-background-100 border border-background-200 rounded-full px-1 py-1 w-fit">
              <button
                type="button"
                onClick={() => handleSort('latest')}
                className={`px-5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  sort === 'latest' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-primary-600'
                }`}
              >
                最新发布
              </button>
              <button
                type="button"
                onClick={() => handleSort('hot')}
                className={`px-5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  sort === 'hot' ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:text-primary-600'
                }`}
              >
                最热阅读
              </button>
            </div>
            <div className="text-sm text-foreground-500">
              共 <span className="text-accent-600 font-medium">{sorted.length}</span> 条动态
            </div>
          </div>

          {visibleItems.length > 0 ? (
            <>
              <div className="flex flex-col gap-4">
                {visibleItems.map((item) => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </div>

              {hasMore && (
                <div className="flex justify-center mt-10">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + LOAD_STEP)}
                    className="px-8 py-3 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    加载更多
                  </button>
                </div>
              )}
              {!hasMore && sorted.length > LOAD_STEP && (
                <p className="text-center text-sm text-foreground-400 mt-10">已经到底啦，看看其他栏目吧</p>
              )}
            </>
          ) : (
            <div className="py-20 text-center">
              <span className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-300">
                <i className="ri-newspaper-line text-3xl"></i>
              </span>
              <p className="text-foreground-600 mt-5">暂无相关资讯</p>
              <Link
                to="/"
                className="mt-5 inline-block px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          )}

          <div className="mt-12 flex justify-center">
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm text-foreground-600 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}